import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { createTurkishTheme } from '../styles/turkish-theme';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Initialize theme mode from localStorage or system preference
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) return savedMode;
    
    // Check system preference if no saved mode
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });

  // Handle system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
        // Apply immediate transition classes
        document.documentElement.style.setProperty('--theme-transition', 'all 0.3s ease-in-out');
        requestAnimationFrame(() => {
          document.documentElement.classList.toggle('dark-theme', e.matches);
          document.documentElement.classList.toggle('light-theme', !e.matches);
        });
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Smooth theme toggle with transition handling
  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      
      // Apply transition classes
      document.documentElement.style.setProperty('--theme-transition', 'all 0.3s ease-in-out');
      requestAnimationFrame(() => {
        document.documentElement.classList.remove(prevMode + '-theme');
        document.documentElement.classList.add(newMode + '-theme');
      });
      
      return newMode;
    });
  }, []);

  // Memoize context value
  // Create Turkish theme based on current mode
  const theme = useMemo(() => createTurkishTheme(mode), [mode]);

  const contextValue = useMemo(() => ({
    mode,
    toggleTheme,
    isDark: mode === 'dark',
    theme
  }), [mode, toggleTheme, theme]);

  // Apply theme classes on mount and changes
  useEffect(() => {
    document.documentElement.classList.remove('light-theme', 'dark-theme');
    document.documentElement.classList.add(`${mode}-theme`);
    document.documentElement.style.setProperty('--theme-transition', 'all 0.3s ease-in-out');
  }, [mode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};
